import React from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { XCircleIcon, DocumentArrowDownIcon, ArchiveBoxIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Toaster from "@/components/Toaster";
import { resetStatus, uploadProducts } from "@/store/slices/productSlice";
import { useDispatch, useSelector } from "react-redux";
import ShowLoader from "@/components/Loader";

// ── CSV validation ────────────────────────────────────────────────────────────
const CSV_MIME_TYPES = [
    "text/csv",
    "application/csv",
    "text/x-csv",
    "application/vnd.ms-excel",
];

const isValidCsvFile = (file) => {
    if (!file || !(file instanceof File)) return false;
    const ext = file.name?.toLowerCase().slice(file.name.lastIndexOf("."));
    return ext === ".csv" || CSV_MIME_TYPES.includes(file.type);
};

// ── ZIP validation ────────────────────────────────────────────────────────────
const ZIP_MIME_TYPES = [
    "application/zip",
    "application/x-zip",
    "application/x-zip-compressed",
    "application/octet-stream",
];

const REQUIRED_ZIP_NAME = "product_images.zip";

const isValidZipFile = (file) => {
    if (!file || !(file instanceof File)) return false;
    const ext = file.name?.toLowerCase().slice(file.name.lastIndexOf("."));
    return ext === ".zip" || ZIP_MIME_TYPES.includes(file.type);
};

// ── Yup schema ────────────────────────────────────────────────────────────────
const schema = (t) => yup.object().shape({
    csv_file: yup
        .mixed()
        .required(t("upload.csvRequired"))
        .test("csvFileType", t("upload.csvType"), (value) => isValidCsvFile(value)),

    zip_file: yup
        .mixed()
        .required(t("upload.zipRequired"))
        .test("zipFileType", t("upload.zipType"), (value) => isValidZipFile(value))
        .test(
            "zipFileName",
            t("upload.zipName", { fileName: REQUIRED_ZIP_NAME }),
            (value) => !value || value.name === REQUIRED_ZIP_NAME
        ),
});

// ── Reusable file drop zone ───────────────────────────────────────────────────
function FileDropZone({ id, accept, icon: Icon, label, hint, file, error, onBoxClick, onFileChange, onRemove }) {
    const { t } = useTranslation("product");
    return (
        <div>
            <label className="block text-sm font-medium mb-2">
                {label} <span className="text-red-600">*</span>
            </label>

            <div
                onClick={onBoxClick}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500 transition"
            >
                <Icon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-700 font-medium">{t("upload.clickToUpload")}</p>
                <p className="text-xs text-gray-500 mt-1">{hint}</p>

                <input
                    type="file"
                    id={id}
                    accept={accept}
                    onChange={onFileChange}
                    className="hidden"
                />
            </div>

            {file && (
                <div className="flex items-center justify-between gap-2 mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-5 w-5 text-gray-500 shrink-0" />
                        <span className="text-sm font-medium text-gray-700 truncate">
                            {file.name}
                        </span>
                        <span className="text-xs text-gray-500 shrink-0">
                            ({t("upload.fileSize", { size: (file.size / 1024).toFixed(1) })})
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        className="p-1 rounded-full text-red-500 hover:bg-red-50 transition shrink-0"
                        aria-label={t("upload.removeFile")}
                    >
                        <XCircleIcon className="w-5 h-5" />
                    </button>
                </div>
            )}

            {error && (
                <p className="text-red-600 text-sm mt-1">{error}</p>
            )}
        </div>
    );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function UploadProductsModal({ isOpen, onClose, onSubmitted }) {
    const dispatch = useDispatch();
    const { t } = useTranslation('product');
    const { t: ct } = useTranslation('common');
    const { loading } = useSelector((state) => state.product);

    const {
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(schema(t)),
        defaultValues: { csv_file: null, zip_file: null },
    });

    const csv_file = watch("csv_file");
    const zip_file = watch("zip_file");

    // ── CSV handlers ──────────────────────────────────────────────────────────
    const handleCsvBoxClick = () => document.getElementById("csv-upload").click();

    const handleCsvChange = (e) => {
        const file = e.target.files?.[0];
        setValue("csv_file", file && isValidCsvFile(file) ? file : null, { shouldValidate: true });
        e.target.value = "";
    };

    const handleCsvRemove = () => {
        setValue("csv_file", null, { shouldValidate: true });
        document.getElementById("csv-upload").value = "";
    };

    // ── ZIP handlers ──────────────────────────────────────────────────────────
    const handleZipBoxClick = () => document.getElementById("zip-upload").click();

    const handleZipChange = (e) => {
        const file = e.target.files?.[0];
        setValue("zip_file", file && isValidZipFile(file) ? file : null, { shouldValidate: true });
        e.target.value = "";
    };

    const handleZipRemove = () => {
        setValue("zip_file", null, { shouldValidate: true });
        document.getElementById("zip-upload").value = "";
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmit = async (data) => {
        try {
            const result = await dispatch(
                uploadProducts({
                    product_csv: data.csv_file,
                    product_images: data.zip_file,
                })
            ).unwrap();

            if (result?.status) {
                Toaster.success(t("upload.success"));
                onClose();
                onSubmitted();
                dispatch(resetStatus());
                reset();
            } else {
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t("upload.failed"));
            dispatch(resetStatus());
        }
    };

    const handleClose = () => {
        reset();
        handleCsvRemove();
        handleZipRemove();
        onClose();
    };

    const bothFilesReady = !!csv_file && !!zip_file;

    return (
        <Dialog open={isOpen} handler={handleClose} size="sm" className="rounded-xl">
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">{t("upload.title")}</h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="px-6 max-h-[65vh] overflow-y-auto space-y-6">

                    {/* ── CSV upload ── */}
                    <FileDropZone
                        id="csv-upload"
                        accept=".csv,text/csv,application/csv"
                        icon={DocumentArrowDownIcon}
                        label={t("upload.csvLabel")}
                        hint={t("upload.csvHint")}
                        file={csv_file}
                        error={errors.csv_file?.message}
                        onBoxClick={handleCsvBoxClick}
                        onFileChange={handleCsvChange}
                        onRemove={handleCsvRemove}
                    />

                    {/* ── ZIP upload ── */}
                    <FileDropZone
                        id="zip-upload"
                        accept=".zip,application/zip,application/x-zip-compressed"
                        icon={ArchiveBoxIcon}
                        label={t("upload.zipLabel")}
                        hint={t("upload.zipHint", { fileName: REQUIRED_ZIP_NAME })}
                        file={zip_file}
                        error={errors.zip_file?.message}
                        onBoxClick={handleZipBoxClick}
                        onFileChange={handleZipChange}
                        onRemove={handleZipRemove}
                    />

                    {/* ── ZIP naming reminder ── */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
                        <p className="font-semibold mb-1">{t("upload.zipRuleTitle")}</p>
                        <p>
                            {t("upload.zipRuleDescription.part1")} <code className="font-mono bg-yellow-100 px-1 rounded">product_images.zip</code> {t("upload.zipRuleDescription.part2")}{" "}
                            <code className="font-mono bg-yellow-100 px-1 rounded">{"{SKU}-{slot}.jpg"}</code>{" "}
                            {t("upload.zipRuleDescription.part3")} <code className="font-mono bg-yellow-100 px-1 rounded">SKU-X10-1.jpg</code>
                        </p>
                    </div>

                </DialogBody>

                <DialogFooter className="flex justify-end gap-3 border-t px-6 py-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition"
                    >
                        {ct("cancel")}
                    </button>

                    <button
                        type="submit"
                        className="px-5 py-2.5 bg-yellow-500 text-white rounded-md font-semibold hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!bothFilesReady}
                    >
                        {ct("upload")}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default UploadProductsModal;